import type React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { type ILabelActions } from '@cx-web/feature-i18n';
import classnames from 'classnames';

import { type Repub } from '@cx-modules/constants';

import Icon from '../../../../primitives/icon/index';
import { useDesktopHeaderTracking } from '../../context/DesktopHeaderTrackingContext';
import caretStyles from '../dropdownCaret.module.css';

import styles from './ProfileDropdown.module.css';

type ProfileDropdownProps = {
  labelActions: ILabelActions;
  onLogout?: () => void;
  // Manage-subscription portal (ProsperStack). Fed from config (ENV.PROSPERSTACK_URL) by the host glue;
  // the literal below is only a fallback for standalone/test renders. Absolute → opens in a new tab.
  prosperStackUrl?: string;
  repub?: Repub;
  siteConfig?: { subscriptions?: { enabled?: boolean } };
};

type ProfileItem = {
  link: string;
  name: string;
  newTab?: boolean;
};

const FALLBACK_PROSPERSTACK_URL = 'https://app.prosperstack.com/p/portal/acct_C26RDWQxovsqswzDoFxz6get';

// My Bookmakers / My Bets have no cx-web page — link back to oc-uk pages.
const OC_UK_BASE = 'https://www.oddschecker.com';

const AccountIcon: React.FC = () => <Icon data-testid="account-icon" height={16} icon="Account" width={16} />;

const buildItems = ({
  labelActions,
  prosperStackUrl = FALLBACK_PROSPERSTACK_URL,
  repub,
  siteConfig,
}: Pick<ProfileDropdownProps, 'labelActions' | 'prosperStackUrl' | 'repub' | 'siteConfig'>): ProfileItem[] => {
  // Internal links: OC (and unknown) stay un-prefixed; every other repub gets a /{repub}/ prefix
  // (repub is uppercase, so lower-case it for the URL).
  const internalPath = (path: string) => (repub && repub !== 'OC' ? `/${repub.toLowerCase()}/${path}` : path);

  return [
    { name: labelActions.get('PROFILE'), link: internalPath('myoddschecker/profile') },
    { name: labelActions.get('SETTINGS'), link: internalPath('myoddschecker/settings') },
    ...(siteConfig?.subscriptions?.enabled
      ? [{ name: labelActions.get('SUBSCRIPTIONS'), link: prosperStackUrl, newTab: true }]
      : []),
    ...(repub === 'OC'
      ? [
          { name: 'My Bookmakers', link: `${OC_UK_BASE}/myoddschecker/bookmakers` },
          { name: 'My Bets', link: `${OC_UK_BASE}/myoddschecker/my-bets` },
        ]
      : []),
  ].filter(item => item.name);
};

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  labelActions,
  onLogout,
  prosperStackUrl,
  repub,
  siteConfig,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Unique per instance so aria-controls/id stays valid if more than one dropdown renders on a page.
  const panelId = useId();
  const track = useDesktopHeaderTracking();

  const items = buildItems({ labelActions, prosperStackUrl, repub, siteConfig });
  const logoutLabel = labelActions.get('LOG_OUT');

  const handleLogout = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsOpen(false);
      track('account', logoutLabel);
      onLogout?.();
    },
    [logoutLabel, onLogout, track],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className={styles.container} data-open={isOpen} data-testid="profile-dropdown" ref={containerRef}>
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-label="Account"
        className={styles.toggle}
        data-testid="profile-dropdown-trigger"
        onClick={() => {
          if (!isOpen) track('account', 'My Account');
          setIsOpen(prev => !prev);
        }}
        type="button"
      >
        <AccountIcon />
        <span aria-hidden="true" className={classnames(caretStyles.caret, isOpen && caretStyles.caretOpen)} />
      </button>

      <div className={styles.panelWrapper}>
        <div className={styles.panel} id={panelId}>
          <ul className={styles.list}>
            {items.map((item, index) => (
              <li className={styles.item} key={item.link || item.name || index}>
                <a
                  className={styles.link}
                  href={item.link}
                  onClick={() => track('account', item.name)}
                  rel={item.newTab ? 'noopener noreferrer' : undefined}
                  target={item.newTab ? '_blank' : undefined}
                >
                  {item.name}
                </a>
              </li>
            ))}
            <li className={styles.item}>
              <button className={styles.logoutButton} onClick={handleLogout} type="button">
                {logoutLabel}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProfileDropdown;
